<script lang="ts">
import type { ListingView } from '#layers/listing/types/listing';
import type { TabsItem } from '#layers/ui/public';
import type { ListingLayoutOption } from './types';

interface ListingViewsProps {
	views: ListingView[];
	defaultView: ListingView;
	resolveLayoutOption: (layoutId: string) => ListingLayoutOption | undefined;
}
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { randomUuid } from '#layers/base/public';
import { useListingViews } from '#layers/listing/composables/use-listing-views';
import { showConfirmationDialog, useCursorAnchor } from '#layers/ui/public';

const props = defineProps<ListingViewsProps>();

const emits = defineEmits<{
	add: [view: ListingView['id']];
	edit: [view: ListingView['id']];
	duplicate: [view: ListingView['id']];
	deleteView: [view: ListingView['id']];
}>();

const appConfig = useAppConfig();
const { t } = useI18n();
const current = defineModel<string>('currentViewId');

const { getViewName } = useListingViews({
	collectionId: null,
	resolveLayoutName: (layoutId) => props.resolveLayoutOption(layoutId)?.name || layoutId,
});

// Combine default view with user views
const allViews = computed(() => [props.defaultView, ...props.views]);

const items = computed<TabsItem[]>(() => allViews.value.map((view) => {
	const layoutOption = props.resolveLayoutOption(view.layoutId);
	return {
		value: view.id,
		label: getViewName(view),
		icon: view.icon ?? layoutOption?.icon ?? undefined,
	};
}));

function createView() {
	const newViewId: ListingView['id'] = randomUuid();
	emits('add', newViewId);
	current.value = newViewId;
}

async function handleDelete(viewId: string) {
	const view = allViews.value.find((v) => v.id === viewId);
	if (!view) return;

	const viewName = getViewName(view);

	const confirmed = await showConfirmationDialog({
		props: {
			title: t('listing.views.delete.question', { name: viewName }),
			confirm: { color: 'error', label: t('listing.views.delete.confirm') },
		},
	});

	if (confirmed) {
		emits('deleteView', viewId);
	}
}

function getContextMenuItems(viewId: string) {
	const isDefaultView = viewId === props.defaultView.id;

	return [
		{
			label: t('common.edit'),
			icon: 'lucide:pencil',
			onSelect: () => emits('edit', viewId),
		},
		{
			label: t('common.duplicate'),
			icon: 'lucide:copy',
			onSelect: () => emits('duplicate', viewId),
		},
		{
			label: t('common.delete'),
			icon: 'lucide:trash',
			color: 'error' as const,
			disabled: isDefaultView,
			onSelect: () => handleDelete(viewId),
		},
	];
}

const contextMenuOpen = ref(false);
const contextMenuViewId = ref<string | null>(null);
const { reference: virtualReference, anchorTo: anchorContextMenu } = useCursorAnchor();

const currentContextMenuItems = computed(() => {
	if (!contextMenuViewId.value) return [];
	return getContextMenuItems(contextMenuViewId.value);
});

// Handle right-click on tabs
function handleTabContextMenu(event: MouseEvent) {
	const tabButton = (event.target as HTMLElement).closest('[role="tab"]');
	const viewId = tabButton?.getAttribute('id')?.split('-trigger-')[1];

	if (!viewId) return;

	event.preventDefault();
	event.stopPropagation();

	contextMenuViewId.value = viewId;
	anchorContextMenu(event);
	contextMenuOpen.value = true;
}

function handleContextMenuClose(open: boolean) {
	if (!open) {
		contextMenuViewId.value = null;
	}
}
</script>

<template>
	<div class="flex min-w-0 flex-1 items-center gap-2">
		<DTabs
			v-model="current"
			:items
			:ui="{ root: 'gap-0' }"
			size="sm"
			collapsible
			@contextmenu="handleTabContextMenu"
		/>
		<DTooltip :text="$t('listing.views.add_new')">
			<DButton
				variant="ghost"
				color="neutral"
				:leading-icon="appConfig.ui.icons.plus"
				:aria-label="$t('listing.views.add_new')"
				size="sm"
				@click="createView"
			/>
		</DTooltip>
	</div>
	<DDropdownMenu
		v-model:open="contextMenuOpen"
		:items="currentContextMenuItems"
		:content="{ reference: virtualReference }"
		size="sm"
		@update:open="handleContextMenuClose"
	/>
</template>
